import {zodResolver} from '@hookform/resolvers/zod';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import {Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack} from '@mui/material';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import {z} from 'zod';
import {useForm} from 'react-hook-form';
import {errorMap} from '@/utils/zod';
import {RhfTextField} from 'mui-rhf-integration';
import {useUpdateChangeRequest} from '@/mutations/useUpdateChangeRequest';
import type {MutateFunctionError} from '@/types/MutateFunctionError';
import {useUserContext} from '@/components/Providers/UserContext';
import type * as React from 'react';
import {useEffect, useState} from 'react';
import type {CourseApproval} from '@/types/CourseApproval';

export type RequestModalState = {
    open : boolean;
    courseApproval ?: CourseApproval,
    title ?: string;
};

const schema = z.object({
    id: z.string(),
    changeRequest: z.string(),
});

export type EditMembershipFormValues = z.infer<typeof schema>;

type Props = {
    modalState: RequestModalState;
    setModalState : (modalState: RequestModalState) => void;
};

const DialogChangeRequestForm = ({modalState, setModalState} : Props) : React.ReactElement => {
    const {user} = useUserContext();
    const [errorMessage, setErrorMessage] = useState('');
    const updateChangeRequestMutation = useUpdateChangeRequest();
    const updateChangeRequestMutationError = updateChangeRequestMutation.error as MutateFunctionError;

    const form = useForm<EditMembershipFormValues>({
        resolver: zodResolver(schema, {errorMap}),
        defaultValues: {},
    });

    const handleClose = () => {
        setModalState({
            ...modalState,
            open: false
        });
    };

    const handleSubmit = async (values : EditMembershipFormValues) => {
        if (user && modalState.courseApproval) {
            updateChangeRequestMutation.mutate({
                jwt: user.jwt,
                courseNumber: modalState.courseApproval.courseNumber,
                sectionId: modalState.courseApproval.sectionId,
                request: values.changeRequest,
            });
        }
    };

    useEffect(() => {
        if (updateChangeRequestMutation.isSuccess) {
            const updateChangeRequestMutationResponse = updateChangeRequestMutation.data;

            if (updateChangeRequestMutationResponse.errorCode !== 0) {
                if (updateChangeRequestMutationResponse.message !== '') {
                    setErrorMessage(updateChangeRequestMutationResponse.message);
                    return;
                }

                setErrorMessage(`Unknown Error: Error Code: ${updateChangeRequestMutationResponse.errorCode}`);
                return;
            }

            setModalState({
                ...modalState,
                open: false,
            });
        }
    }, [updateChangeRequestMutation.isSuccess]);

    useEffect(() => {
        setErrorMessage('');
        if (modalState.open && modalState.courseApproval) {
            updateChangeRequestMutation.reset();

            if (modalState.courseApproval) {
                form.reset({
                    id: modalState.courseApproval.compoundKey,
                    changeRequest: modalState.courseApproval.request,
                })
            }
        }
    }, [modalState]);

    return <>
        {modalState.open && modalState.courseApproval === undefined && <Alert severity='error'>Course Approval Not Found</Alert>}
        {modalState.courseApproval && <Dialog
            maxWidth='sm'
            fullWidth={true}
            open={modalState.open}
            onClose={handleClose}
        >
            <form onSubmit={form.handleSubmit(handleSubmit)}>
                <DialogTitle>
                    <Stack
                        sx={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Typography variant='h6' fontWeight={800}>{modalState.title}</Typography>
                        <IconButton onClick={handleClose}>
                            <HighlightOffIcon/>
                        </IconButton>
                    </Stack>
                </DialogTitle>
                <DialogContent sx={{pb: 1, minHeight: '200px'}}>
                    <RhfTextField
                        control={form.control}
                        name='changeRequest'
                        label={'Change Request'}
                        fullWidth
                        required
                        multiline
                        rows={6}
                        sx={{
                            mt: 1,
                            height: '100%',
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{pb: 2, pr: 3}}>
                    <Stack
                        sx={{
                            width: '100%',
                            flexDirection: 'column',
                        }}
                    >
                        <Stack
                            sx={{
                                mb: 2,
                                flexDirection: 'row',
                                justifyContent: 'center',
                            }}
                        >
                            {updateChangeRequestMutation.isError || errorMessage !== '' && <Alert severity='error'>
                                {updateChangeRequestMutationError?.message ?? errorMessage}
                            </Alert>}
                        </Stack>
                        <Stack
                            sx={{
                                flexDirection: 'row',
                                justifyContent: 'flex-end',
                            }}
                        >
                            <Button
                                variant='outlined'
                                onClick={() => {
                                    setModalState({
                                        ...modalState,
                                        open: false,
                                    });
                                }}
                                sx={{
                                    mr: 1,
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant='contained'
                                type='submit'
                            >
                                Submit Request
                            </Button>
                        </Stack>
                    </Stack>
                </DialogActions>
            </form>
        </Dialog>}
    </>;
};

export default DialogChangeRequestForm;
